import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import ValueBadge from '../components/ValueBadge';
import VibeTags from '../components/VibeTags';
import { useTripBuilder } from '../TripBuilderContext';

export default function Compare({ compareIds }) {
  const { amexTotal: amex } = useTripBuilder();
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all(compareIds.map(id => api.getDestination(id)))
      .then(setDestinations)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [compareIds]);

  if (compareIds.length < 2) {
    return (
      <div className="text-center py-16">
        <div className="text-brand-ink/65 text-lg mb-4">Select at least 2 destinations to compare</div>
        <Link to="/" className="text-teal hover:underline">Back to Command Center</Link>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center h-64 text-brand-ink/65">Loading comparison…</div>;

  const rows = [
    {
      label: 'Best Points Deal',
      render: (d) => {
        const r = d.redemption_options?.[0];
        return r ? (
          <div>
            <div className="font-semibold text-sm">{r.airline_program}</div>
            <div className="text-xs text-brand-ink/70">{r.total_points_two_travelers?.toLocaleString()} pts</div>
          </div>
        ) : '—';
      }
    },
    {
      label: 'Value (cpp)',
      render: (d) => {
        const r = d.redemption_options?.[0];
        return r ? <ValueBadge cpp={parseFloat(r.cents_per_point)} /> : '—';
      }
    },
    {
      label: 'Amex Points Used',
      render: (d) => {
        const r = d.redemption_options?.[0];
        return r ? <span className="font-semibold">{r.total_points_two_travelers?.toLocaleString()}</span> : '—';
      }
    },
    {
      label: 'Amex Remaining',
      render: (d) => {
        const r = d.redemption_options?.[0];
        const remaining = r ? amex - r.total_points_two_travelers : amex;
        return <span className={remaining > 150000 ? 'text-teal font-semibold' : 'font-semibold'}>{remaining.toLocaleString()}</span>;
      }
    },
    {
      label: 'Hotel Play',
      render: (d) => {
        const h = d.hotel_options?.find(h => h.is_recommended);
        return h ? (
          <div>
            <div className="text-sm">{h.hotel_program}</div>
            <div className="text-xs text-brand-ink/70">{h.total_points?.toLocaleString()} Chase pts</div>
          </div>
        ) : <span className="text-brand-ink/55">Portal only</span>;
      }
    },
    {
      label: 'Taxes (flights)',
      render: (d) => {
        const r = d.redemption_options?.[0];
        return r ? `$${r.total_estimated_taxes}` : '—';
      }
    },
    {
      label: 'Daily Budget',
      render: (d) => `$${d.avg_daily_budget_min}-${d.avg_daily_budget_max}/day`,
    },
    {
      label: 'Transfer Speed',
      render: (d) => {
        const r = d.redemption_options?.[0];
        return r ? <span className={r.transfer_time === 'Instant' ? 'text-teal' : 'text-gold'}>{r.transfer_time}</span> : '—';
      }
    },
    {
      label: 'Best Seat',
      render: (d) => {
        const r = d.redemption_options?.[0];
        return r?.seat_product || '—';
      }
    },
    {
      label: 'Weather (Sep)',
      render: (d) => `${d.weather_high_f}°F, ${d.weather_rain_chance} rain`,
    },
    {
      label: 'Vibes',
      render: (d) => <VibeTags tags={d.vibe_tags} size="xs" />,
    },
  ];

  // Find the best overall (lowest points cost with good value)
  const bestIdx = destinations.reduce((best, d, i) => {
    const r = d.redemption_options?.[0];
    const score = (parseFloat(r?.cents_per_point) || 0) * 100 - (r?.total_points_two_travelers || 999999) / 10000;
    const bestR = destinations[best]?.redemption_options?.[0];
    const bestScore = (parseFloat(bestR?.cents_per_point) || 0) * 100 - (bestR?.total_points_two_travelers || 999999) / 10000;
    return score > bestScore ? i : best;
  }, 0);

  return (
    <div className="space-y-6 pb-16">
      <Link to="/explore" className="text-sm text-brand-ink/65 hover:text-brand-ink/75">&larr; Back to Explore</Link>
      <h1 className="text-2xl font-bold">Side-by-Side</h1>
      <p className="text-brand-ink/65 text-sm -mt-4">Compare the destinations you flagged.</p>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="text-left p-3 text-xs text-brand-ink/65 font-medium w-40"></th>
              {destinations.map((d, i) => (
                <th key={d.id} className="p-3 text-left">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-lg">{d.city}</span>
                    {i === bestIdx && (
                      <span className="text-xs bg-teal/20 text-teal px-2 py-0.5 rounded-full font-semibold">BEST PICK</span>
                    )}
                  </div>
                  <div className="text-sm text-brand-ink/70">{d.country}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white/[0.02]' : ''}>
                <td className="p-3 text-xs text-brand-ink/65 font-medium">{row.label}</td>
                {destinations.map(d => (
                  <td key={d.id} className="p-3 text-sm">{row.render(d)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center gap-3 pt-4">
        {destinations.map(d => (
          <Link
            key={d.id}
            to={`/destination/${d.id}`}
            className="px-4 py-2 bg-teal/20 text-teal rounded-lg text-sm font-semibold hover:bg-teal/30 transition"
          >
            Explore {d.city}
          </Link>
        ))}
      </div>
    </div>
  );
}
